import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { ProgressIcon } from '@src/screens/ActionGoals/components/ProgressIcon'
import { Icon, IconProps, Text } from '@src/components/base'
import { ActionGoalCompletion } from '@src/types'
import { SUMMARY_CARD_HEIGHT } from '../constants'
import { calculateProgress } from '../utils'
import { ActionGoalCompletionMarkers } from './ActionGoalCompletionMarkers'

interface ActionGoalSummaryCardProps {
  onViewDetailsPress: () => void
  onCardPress?: () => void
  completions: ActionGoalCompletion[]
  showTrend: boolean
  numberDays: number
  title: string
  description: string
  iconName: IconProps['name']
  cardHeight?: number
}

export const ActionGoalSummaryCard = ({
  onViewDetailsPress,
  onCardPress,
  completions,
  showTrend,
  numberDays,
  title,
  description,
  iconName,
  cardHeight = SUMMARY_CARD_HEIGHT,
}: ActionGoalSummaryCardProps) => {
  const styles = useStyleSheet(themedStyles)

  const daysCompletedCount = completions.length

  const progress = calculateProgress(daysCompletedCount, numberDays)

  return (
    <TouchableOpacity
      onPress={onCardPress}
      disabled={!onCardPress}
      style={[styles.container, { height: cardHeight }]}
      accessibilityLabel="View goal details"
    >
      <View style={styles.contentContainer}>
        <View style={styles.progressIconContainer}>
          <ProgressIcon progress={progress} size={48} name={iconName} />
          <Text
            type="small"
            style={styles.progressText}
          >{`${daysCompletedCount} / ${numberDays} `}</Text>
        </View>
        <View style={styles.textAndDaysContainer}>
          <View style={styles.titleRow}>
            <Text type="regular" numberOfLines={1}>
              {title}
            </Text>
          </View>
          <View style={styles.descriptionAndCompletionsContainer}>
            <Text type="small" numberOfLines={2} style={styles.descriptionText}>
              {description}
            </Text>
            <View style={styles.dayCompletionsContainer}>
              <ActionGoalCompletionMarkers completions={completions} />
            </View>
          </View>
        </View>
        <View style={styles.iconsColumn}>
          <TouchableOpacity
            accessibilityLabel="View Goal Details"
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            onPress={() => {
              onViewDetailsPress()
            }}
          >
            <Icon name="gear" size={20} />
          </TouchableOpacity>
          <View style={styles.trendIconContainer}>
            {showTrend && <Icon name="caret-right" size={16} />}
          </View>
        </View>
      </View>
    </TouchableOpacity>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    borderWidth: 2,
    borderRadius: 12,
    borderColor: 'theme.surface.base1',
    backgroundColor: 'theme.surface.base3',
  },
  progressIconContainer: {
    alignItems: 'center',
    marginRight: 12,
  },
  progressText: {
    marginTop: 4,
    textAlign: 'center',
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'row',
    padding: 10,
  },
  textAndDaysContainer: {
    flexDirection: 'column',
    flexShrink: 1,
    width: '100%',
    maxWidth: 300,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  descriptionAndCompletionsContainer: {
    flex: 1,
    justifyContent: 'space-between',
  },
  descriptionText: {
    color: 'theme.text.secondary',
  },
  dayCompletionsContainer: {
    flexDirection: 'row',
  },
  iconsColumn: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  trendIconContainer: {
    justifyContent: 'center',
    marginBottom: 10,
    flex: 1,
  },
})
